import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BroadcastService } from 'src/app/shared/services/broadcast.service';
import { L10nService } from './l10n.service';
import { MessageEvent } from '../models/broadcast.model';

export enum MessageSeverity {
  SUCCESS,
  ERROR,
  INFO,
  WARNING
}

export class Message {
  severity: MessageSeverity;
  message: string;
  messageId: string;
  keepAfterRouteChange: boolean;

  constructor(init?: Partial<Message>) {
      Object.assign(this, init);
  }
}

@Injectable({
  providedIn: 'root'
})
export class MessageService {

  private subject = new Subject<Message>();
  private keepAfterRouteChange = false;

  constructor(
      router: Router,
      broadcastService: BroadcastService,
      l10nService: L10nService) {

    // clear messages on route change unless 'keepAfterRouteChange' flag is true
    router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (this.keepAfterRouteChange) {
          // only keep for a single route change
          this.keepAfterRouteChange = false;
        } else {
          // clear messages
          this.clear();
        }
      }
    });

    broadcastService.on<MessageEvent>(MessageEvent.NAME).subscribe(message =>
      this.message(new Message({
        message: l10nService.get(message.getMessageKey(), message.getMessageParams()),
        severity: message.getSeverity()
      })));
  }

  // subscribe to Messages
  getMessage(messageId?: string): Observable<any> {
    return this.subject.pipe(filter((x: Message) => x && x.messageId === messageId));
  }

  // convenience methods
  success(message: string) {
    this.message(new Message({ message, severity: MessageSeverity.SUCCESS }));
  }

  error(message: string) {
    this.message(new Message({ message, severity: MessageSeverity.ERROR }));
  }

  info(message: string) {
    this.message(new Message({ message, severity: MessageSeverity.INFO }));
  }

  warning(message: string) {
    this.message(new Message({ message, severity: MessageSeverity.WARNING }));
  }

  // main message method
  private message(message: Message) {
    this.keepAfterRouteChange = message.keepAfterRouteChange;
    this.subject.next(message);
  }

  // clear Messages
  clear(messageId?: string) {
    this.subject.next(new Message({ messageId }));
  }
}
